import { createSelector } from '@reduxjs/toolkit'

import type { QueryResultLike, QueryView } from '@krp/client-runtime'

import { sameId } from '../../../shared/api/wire'
import type { CommandFeedback } from '../../../shared/model/feedback'
import { formatKopecks } from '../../../shared/model/format'
import { formatMoment, makeQueryViewSelector, readyData } from '../../../shared/model/query'
import { formatInstant, formatQuantity } from '../../../shared/uc1b/format'
import type { ReceiptDetail } from '../../receipts/model/types'
import type { AqsiSession } from '../../rmk/model/types'
import { attentionLines, isSlipOperation } from '../../sale/model/selectors'
import type { AqsiSaleOperation, FiscalState, PaymentState, SaleOperationVm, StateBadgeVm } from '../../sale/model/types'
import { fractionalAllowed, refundQuantityError, thousandths } from './quantity'
import { blocksRefundCommands } from './refundCommandsSlice'
import type {
  AqsiRefund,
  RefundCommand,
  RefundCommandVm,
  RefundDraft,
  RefundProgressVm,
  RefundSourceLineVm,
  RefundVm,
  SeatActive,
} from './types'

/**
 * Вход vm окна возврата — НЕ RootState: binding отдаёт результаты запросов
 * одного rid и намерение этого чека.
 */
export interface RefundVmInput {
  resourceId: string
  receiptId: string
  receipt: QueryResultLike<ReceiptDetail>
  /** Моя работа за кассой — из нашей базы (`sessions/mine`), кабинет не нужен. */
  mySession: QueryResultLike<AqsiSession | null>
  /** Что на кассе моего места; `undefined` — места нет (работа не начата). */
  seat: QueryResultLike<SeatActive> | undefined
  operation: QueryResultLike<AqsiSaleOperation> | undefined
  finished: QueryResultLike<AqsiRefund> | undefined
  finishedRefundId: string | null
  /** Служба считает возвращённое по чеку (`capabilities.refunds` журнала этого rid); не известно — 0 не значит «не было». */
  refundsKnown?: boolean
  draft: RefundDraft
  command: RefundCommand | undefined
  feedback: CommandFeedback | undefined
}

/**
 * Операция возврата, ход которой показывает окно: идущие/неизвестные деньги,
 * затем идущий/неизвестный чек; доказанный отказ — тоже (его текст важен).
 */
export function refundOperationOf(seat: SeatActive | undefined): string | null {
  if (seat?.kind !== 'refund') return null
  const refund = seat.refund
  if (refund.purchaseOperationId !== null && ['InProgress', 'Unknown', 'Failed'].includes(refund.payment)) {
    return refund.purchaseOperationId
  }
  if (refund.receiptOperationId !== null && ['InProgress', 'Unknown', 'Failed'].includes(refund.fiscal)) {
    return refund.receiptOperationId
  }
  return null
}

const MONEY: Readonly<Record<PaymentState, Omit<StateBadgeVm, 'label'>>> = {
  NotRequired: { text: 'Наличные — терминал не нужен, деньги выдаются из кассы', tone: 'neutral', icon: 'idle' },
  Pending: { text: 'Деньги не возвращены', tone: 'neutral', icon: 'idle' },
  InProgress: { text: 'Возврат на карту идёт на терминале', tone: 'neutral', icon: 'progress' },
  Succeeded: { text: 'Деньги возвращены', tone: 'success', icon: 'ok' },
  Failed: { text: 'Возврат на карту не прошёл — деньги не возвращены', tone: 'danger', icon: 'failed' },
  Unknown: { text: 'Исход возврата денег неизвестен — повтор запрещён', tone: 'warning', icon: 'unknown' },
}

const RECEIPT: Readonly<Record<FiscalState, Omit<StateBadgeVm, 'label'>>> = {
  Pending: { text: 'Чек возврата не пробит', tone: 'neutral', icon: 'idle' },
  InProgress: { text: 'Чек возврата пробивается', tone: 'neutral', icon: 'progress' },
  Succeeded: { text: 'Чек возврата пробит', tone: 'success', icon: 'ok' },
  Failed: { text: 'Чек возврата не пробит — касса отказала', tone: 'danger', icon: 'failed' },
  Unknown: { text: 'Исход чека возврата неизвестен — повтор запрещён', tone: 'warning', icon: 'unknown' },
  // Договор возврата этого значения не отдаёт (декодер его отвергнет); запись — для полноты типа чека.
  IssuedOutside: { text: 'Чек возврата пробит на кассе вне программы', tone: 'success', icon: 'ok' },
}

const OPERATION_STATE: Readonly<Record<AqsiSaleOperation['state'], { text: string; tone: SaleOperationVm['tone'] }>> = {
  Pending: { text: 'записана службой, отправляется в кабинет', tone: 'neutral' },
  Sent: { text: 'отправлена — касса выполняет', tone: 'neutral' },
  Succeeded: { text: 'выполнена', tone: 'success' },
  Failed: { text: 'не выполнена', tone: 'danger' },
  Unknown: { text: 'исход неизвестен', tone: 'warning' },
}

const KIND_TEXT: Readonly<Record<AqsiSaleOperation['kind'], string>> = {
  purchase: 'Списание с карты',
  receipt: 'Чек',
  refundPayment: 'Возврат на карту',
  refundReceipt: 'Чек возврата',
}

function operationDetails(operation: AqsiSaleOperation): string[] {
  const resolution = operation.resolution
  return [
    operation.providerStatus !== null ? `Кабинет: ${operation.providerStatus}` : null,
    operation.providerMessage,
    operation.note,
    operation.resultId !== null ? `${isSlipOperation(operation.kind) ? 'Слип' : 'Чек'}: ${operation.resultId}` : null,
    operation.sentAt !== null ? `Отправлена: ${formatMoment(operation.sentAt)}` : null,
    operation.checkedAt !== null ? `Проверена: ${formatMoment(operation.checkedAt)}` : null,
    resolution !== null
      ? `Сверено вручную: ${resolution.resolvedBy}, ${formatMoment(resolution.at)} — ${resolution.reason}`
      : null,
  ].filter((line): line is string => line !== null && line !== '')
}

/** Статус операции возврата: то же чтение журнала службы, что у продажи; «отправить ещё раз» в нём нет. */
function operationVm(operationId: string | null, view: QueryView | null, status: AqsiSaleOperation | undefined): SaleOperationVm | null {
  if (operationId === null) return null
  const current = view !== null ? readyData(view, status) : undefined
  if (current === undefined || !sameId(current.operationId, operationId)) {
    return {
      operationId,
      kindText: 'Операция возврата',
      stateText: view?.status === 'loading' || view?.status === 'idle' ? 'статус читается…' : 'статус не прочитан',
      tone: 'warning',
      details: view?.error != null ? [view.error.message] : [],
      version: null,
      canReconcile: false,
      manualRequired: false,
      polling: false,
      accepted: false,
    }
  }
  const state = OPERATION_STATE[current.state]
  const unresolved = current.state !== 'Succeeded' && current.state !== 'Failed'
  return {
    operationId,
    kindText: KIND_TEXT[current.kind],
    stateText: state.text,
    tone: state.tone,
    details: operationDetails(current),
    version: current.version,
    canReconcile: unresolved && current.nextAction === 'Reconcile',
    manualRequired: unresolved && current.nextAction === 'ManualReconciliation',
    polling: unresolved && current.nextAction === 'Reconcile',
    accepted: current.state === 'Sent',
  }
}

const COMMAND_KIND_TEXT: Readonly<Record<RefundCommand['kind'], string>> = {
  create: 'Возврат',
  pay: 'Возврат денег',
  fiscalize: 'Чек возврата',
  cancel: 'Отмена возврата',
}

function commandVm(command: RefundCommand | undefined, receiptId: string): RefundCommandVm | null {
  // Команда кассы по ДРУГОМУ чеку видна в его окне; здесь — только причина блокировки кнопок.
  if (command === undefined || command.receiptId !== receiptId) return null
  const kindText = COMMAND_KIND_TEXT[command.kind]
  switch (command.phase) {
    case 'sending':
      return { kindText, phase: command.phase, text: 'Команда отправлена, ждём ответа службы…' }
    case 'unknown':
      return {
        kindText,
        phase: command.phase,
        text: 'Ответ не получен: неизвестно, дошла ли команда. Повтор запрещён — нажмите «Проверить статус»: служба могла ещё не записать операцию.',
      }
    case 'not-arrived':
      return {
        kindText,
        phase: command.phase,
        text: 'Служба так и не узнала эту команду — касса не изменилась. Можно отдать команду заново: это новая операция.',
      }
  }
}

function paymentTextOf(detail: ReceiptDetail): string {
  const receipt = detail.receipt
  if (receipt.cashKopecks > 0 && receipt.cashlessKopecks > 0) return 'частью наличными, частью картой'
  return receipt.cashlessKopecks > 0 ? 'картой — деньги вернутся на ту же карту' : 'наличными — деньги выдаются из кассы'
}

function sourceLines(detail: ReceiptDetail, draft: RefundDraft): RefundSourceLineVm[] {
  return detail.lines.map((line) => {
    const left = Math.max(0, thousandths(line.quantity) - thousandths(line.returnedQuantity))
    const available = left / 1000
    const quantity = draft.quantities[String(line.ordinal)] ?? ''
    return {
      ordinal: line.ordinal,
      name: line.name,
      soldText: formatQuantity(line.quantity),
      returnedText: line.returnedQuantity > 0 ? formatQuantity(line.returnedQuantity) : 'нет',
      availableText: formatQuantity(available),
      availableValue: String(available),
      priceText: formatKopecks(line.priceKopecks),
      quantity,
      error: quantity === '' ? null : refundQuantityError(quantity, available, fractionalAllowed(line.unitCode, line.quantity)),
      exhausted: available <= 0,
    }
  })
}

function progressVm(refund: AqsiRefund): RefundProgressVm {
  return {
    id: refund.id,
    totalText: formatKopecks(refund.totalKopecks),
    paymentText: refund.paymentKind === 'Card' ? 'на карту покупателя' : 'наличными из кассы',
    lines: refund.lines.map((line) => ({
      key: line.id,
      name: line.name,
      quantityText: line.quantity,
      amountText: formatKopecks(line.amountKopecks),
    })),
    money: { label: 'Деньги', ...MONEY[refund.payment] },
    receipt: { label: 'Чек возврата', ...RECEIPT[refund.fiscal] },
    lastMessage: refund.lastMessage,
    slipText: refund.slipId !== null ? `Слип возврата: ${refund.slipId}` : null,
    attentionLines: attentionLines(refund.attention),
    version: refund.version,
  }
}

export const REVERSAL_NOTE =
  'Отмена оплаты до сверки итогов (reversal) службой не поддерживается: в договоре C4b её нет. Возврат и отмена возврата — разные операции: отменить можно только возврат, по которому деньги не двигались и чек не пробит.'

/** Фабрика: один экземпляр на смонтированный binding (D07). */
export const makeSelectRefundVm = () => {
  const selectReceiptView = makeQueryViewSelector(
    (input: RefundVmInput) => input.receipt,
    () => false,
  )
  const selectRegistersView = makeQueryViewSelector(
    (input: RefundVmInput) => input.mySession,
    () => false,
  )
  const selectSeatView = makeQueryViewSelector(
    (input: RefundVmInput) => input.seat,
    () => false,
  )
  const selectOperationView = makeQueryViewSelector(
    (input: RefundVmInput) => input.operation,
    () => false,
  )
  const selectOperation = createSelector(
    [
      (input: RefundVmInput) => refundOperationOf(input.seat?.data),
      (input: RefundVmInput) => (input.operation === undefined ? null : selectOperationView(input)),
      (input: RefundVmInput) => input.operation?.data,
    ],
    operationVm,
  )
  const selectLines = createSelector(
    [selectReceiptView, (input: RefundVmInput) => input.receipt.data, (input: RefundVmInput) => input.draft],
    (view, data, draft) => {
      const detail = readyData(view, data)
      return detail === undefined ? [] : sourceLines(detail, draft)
    },
  )
  const selectCommand = createSelector(
    [(input: RefundVmInput) => input.command, (input: RefundVmInput) => input.receiptId],
    commandVm,
  )
  const selectFinished = createSelector(
    [(input: RefundVmInput) => input.finishedRefundId, (input: RefundVmInput) => input.finished?.data],
    (refundId, refund) =>
      refundId === null || refund === undefined || !sameId(refund.id, refundId)
        ? null
        : {
            receiptId: refund.receiptId,
            totalText: formatKopecks(refund.totalKopecks),
            paymentText: refund.paymentKind === 'Card' ? 'на карту' : 'наличными',
            cancelled: refund.fiscal !== 'Succeeded',
          },
  )
  const selectProgress = createSelector(
    [selectSeatView, (input: RefundVmInput) => input.seat?.data, (input: RefundVmInput) => input.receiptId],
    (view, data, receiptId) => {
      const seat = readyData(view, data)
      return seat?.kind === 'refund' && seat.refund.sourceReceiptId === receiptId ? progressVm(seat.refund) : null
    },
  )

  return createSelector(
    [
      (input: RefundVmInput) => input.resourceId,
      (input: RefundVmInput) => input.receiptId,
      (input: RefundVmInput) => input.receipt.data,
      (input: RefundVmInput) => input.mySession.data,
      (input: RefundVmInput) => input.seat?.data,
      (input: RefundVmInput) => input.seat !== undefined,
      (input: RefundVmInput) => input.command,
      (input: RefundVmInput) => input.feedback ?? null,
      (input: RefundVmInput) => input.refundsKnown === true,
      selectReceiptView,
      selectRegistersView,
      selectSeatView,
      selectLines,
      selectOperation,
      selectCommand,
      selectFinished,
      selectProgress,
    ],
    (
      resourceId,
      receiptId,
      receiptData,
      registersData,
      seatData,
      hasSeat,
      pending,
      feedback,
      refundsKnown,
      receiptQuery,
      registersQuery,
      seatView,
      lines,
      operation,
      command,
      finished,
      refund,
    ): RefundVm => {
      const detail = readyData(receiptQuery, receiptData)
      const mySession = readyData(registersQuery, registersData)
      const seat = hasSeat ? readyData(seatView, seatData) : undefined
      const blocked = blocksRefundCommands(pending)
      const base = {
        resourceId,
        receiptId,
        receiptQuery,
        title: `Возврат по чеку ${receiptId}`,
        header:
          detail === undefined
            ? null
            : {
                atText: formatInstant(detail.receipt.at),
                totalText: formatKopecks(detail.receipt.totalKopecks),
                paymentText: paymentTextOf(detail),
                returnedText:
                  detail.receipt.returnedKopecks > 0
                    ? `уже возвращено ${formatKopecks(detail.receipt.returnedKopecks)}`
                    : refundsKnown
                      ? 'возвратов по чеку ещё не было'
                      : 'не рассчитано — остаток проверит служба',
              },
        lines: [] as RefundSourceLineVm[],
        canCreate: false,
        createBlockedReason: null,
        refund: null,
        canPay: false,
        payText: '',
        canFiscalize: false,
        canCancel: false,
        operation: null,
        command,
        finished: null,
        reversalNote: REVERSAL_NOTE,
        feedback,
      }
      const blockedVm = (reason: string, action: RefundVm['blockedAction'], seatQuery: QueryView | null = null): RefundVm => ({
        ...base,
        seatQuery,
        stage: 'blocked',
        blockedReason: reason,
        blockedAction: action,
      })
      const loading = (seatQuery: QueryView | null): RefundVm => ({
        ...base,
        seatQuery,
        stage: 'loading',
        blockedReason: null,
        blockedAction: null,
      })

      if (detail === undefined) return loading(null)
      if (detail.receipt.isRefund) return blockedVm('Это чек возврата: вернуть по нему нечего.', null)
      if (mySession === undefined) return loading(registersQuery)
      if (mySession === null) {
        return blockedVm('Вы не работаете за кассой этого кабинета — начните работу в РМК, затем оформляйте возврат.', {
          kind: 'rmk',
        })
      }
      if (seat === undefined) return loading(seatView)
      if (seat.kind === 'sale') {
        return blockedVm('На кассе незавершённая продажа — завершите или отмените её, затем оформляйте возврат.', {
          kind: 'sale',
        })
      }
      if (seat.kind === 'refund' && seat.refund.sourceReceiptId !== receiptId) {
        return blockedVm(
          `На кассе идёт возврат по чеку ${seat.refund.sourceReceiptId} — завершите или отмените его.`,
          { kind: 'refund', receiptId: seat.refund.sourceReceiptId },
        )
      }

      if (refund !== null && seat.kind === 'refund') {
        const current = seat.refund
        const card = current.paymentKind === 'Card'
        const payStage =
          !current.isFinished &&
          (card
            ? ['Pending', 'Failed'].includes(current.payment) && current.fiscal === 'Pending'
            : current.payment === 'NotRequired' && current.fiscal === 'Pending')
        const fiscalizeStage =
          !current.isFinished &&
          ['Succeeded', 'NotRequired'].includes(current.payment) &&
          (card ? ['Pending', 'Failed'].includes(current.fiscal) : current.fiscal === 'Failed')
        const cancelStage =
          !current.isFinished &&
          ['Pending', 'Failed', 'NotRequired'].includes(current.payment) &&
          ['Pending', 'Failed'].includes(current.fiscal)
        return {
          ...base,
          seatQuery: seatView,
          stage: 'refund',
          blockedReason: null,
          blockedAction: null,
          refund,
          canPay: payStage && !blocked,
          payText: card ? `Вернуть на карту ${refund.totalText}` : `Выдать наличные ${refund.totalText} и пробить чек`,
          canFiscalize: fiscalizeStage && !blocked,
          canCancel: cancelStage && !blocked,
          operation,
        }
      }

      if (finished !== null) {
        return { ...base, seatQuery: seatView, stage: 'finished', blockedReason: null, blockedAction: null, finished }
      }

      const chosen = lines.filter((line) => line.quantity !== '')
      let createBlockedReason: string | null = null
      if (!detail.isFiscal) createBlockedReason = 'Чек не фискальный — возврат оформляется только по фискальному чеку'
      else if (blocked) {
        createBlockedReason =
          pending?.phase === 'sending' ? 'Команда отправлена — ждём ответа службы' : 'Исход прошлой команды неизвестен — сначала проверьте статус'
      } else if (lines.length > 0 && lines.every((line) => line.exhausted)) {
        createBlockedReason = 'По чеку всё уже возвращено'
      } else if (chosen.length === 0) createBlockedReason = 'Отметьте, что возвращается: количество у позиции'
      else if (chosen.some((line) => line.error !== null)) createBlockedReason = 'Исправьте количество у отмеченных позиций'
      return {
        ...base,
        seatQuery: seatView,
        stage: 'select',
        blockedReason: null,
        blockedAction: null,
        lines,
        canCreate: createBlockedReason === null,
        createBlockedReason,
      }
    },
  )
}
